import { SIGNAL_CLIENT_FILTER_KEYS } from "@shared/flow/filter-fields";
import type { FilterGroup } from "@shared/flow/filter";
import type { QuickCondition } from "./chips/signalQuickFilters";
import type { SignalRowWithComputed } from "./signalsTableConfig";

/**
 * knv.13 — browser-evaluated Signals filters. The server never sees these
 * (`serverFiltersFor`, useSignalsQueries.ts); they are applied here over the
 * fully loaded list, so `filterNeedsEveryPage` makes the table load every page
 * first (the same mechanism as the client group levels).
 *
 * A browser condition may only sit in an AND position (`andOnlyPlacementError`,
 * filter-fields.ts): the Filters editor blocks Apply and saved-view writes 400 on an OR placement, and
 * the quick-filter button is disabled under outer OR (`quickFilterBlockedReason`). So every client
 * condition is ANDed here; an OR placement is unreachable in product. A faithful OR needs a server-side
 * timezone column (documented follow-up).
 */
type ClientPredicate = (row: SignalRowWithComputed, value: string) => boolean;

/** One predicate per `SIGNAL_CLIENT_FILTER_KEYS` entry. `business_hours` reads the status
 *  `localTimeFor` already stamped (getBusinessHoursStatus, unchanged). */
const CLIENT_PREDICATES: Record<string, ClientPredicate> = {
  business_hours: (row, value) => row.local_time.status === value,
};

/** The browser-evaluated conditions across all groups, in encounter order. */
export function clientSignalFilter(filters: FilterGroup[]): QuickCondition[] {
  return filters.flatMap((group) =>
    group.conditions.flatMap((c) =>
      SIGNAL_CLIENT_FILTER_KEYS.includes(c.field) && typeof c.value === "string"
        ? [{ field: c.field, operator: c.operator, value: c.value }]
        : [],
    ),
  );
}

export function filterNeedsEveryPage(filters: FilterGroup[]): boolean {
  return clientSignalFilter(filters).length > 0;
}

export function applyClientSignalFilters(
  rows: SignalRowWithComputed[],
  filters: FilterGroup[],
): SignalRowWithComputed[] {
  const conditions = clientSignalFilter(filters);
  if (conditions.length === 0) return rows;
  return rows.filter((row) =>
    conditions.every((c) => CLIENT_PREDICATES[c.field]?.(row, c.value) ?? true),
  );
}
